import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { TOption, TRadioListOption } from '@treezor/design-system';

import { IWallet } from '../store';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { TCountryCode, IUser } from '@/features/user/store/user.model';

interface IWalletForm {
  user: IUser;
  defaultValues: Partial<IWallet>;
}
const authorizedBic: Partial<Record<TCountryCode, string>> = {
  FR: 'TRZOFR21XXX',
  DE: 'TRZODEB2XXX',
  IT: 'TRZOITM2XXX',
  ES: 'TRZOESM2XXX',
} as const;

export function useWalletForm({ user, defaultValues }: IWalletForm) {
  const { t } = useTranslation();
  const config = useDashboardConfig();

  const walletTypes = config?.settings?.wallets?.types;
  const localIbans = config?.settings?.wallets?.localIban;

  const hasLocalIbanDistributionCountry = localIbans?.length && user?.distributionCountry;

  const userBics = useMemo(
    () => localIbans?.find((iban) => iban.distribCountry === user?.distributionCountry)?.bic || [],
    [localIbans, user?.distributionCountry],
  );

  const defaultBic = hasLocalIbanDistributionCountry
    ? userBics?.length === 1
      ? authorizedBic[userBics[0]]
      : ''
    : authorizedBic.FR;

  const canAddWallet = walletTypes?.length && (hasLocalIbanDistributionCountry ? userBics?.length : true);

  const { watch, setValue, getValues } = useForm<IWallet>({
    mode: 'onChange',
    defaultValues: {
      walletTypeId: walletTypes?.length === 1 ? walletTypes[0].value : undefined,
      userId: user.userId,
      currency: 'EUR',
      bic: defaultBic,
      ...defaultValues,
    },
  });

  const watchWalletTypeId = watch('walletTypeId');
  const watchBic = watch('bic');
  const watchEventName = watch('eventName');

  const isFormDisabled = useMemo(
    () => !watchWalletTypeId || !watchBic || !watchEventName,
    [watchBic, watchEventName, watchWalletTypeId],
  );

  const walletTypeOptions: TRadioListOption[] = useMemo(
    () =>
      defaultValues.walletTypeId === 14
        ? [
            {
              label: t(`pages.currentProfiles.actionUser.addWallet.form.walletTypeOptions.14`),
              value: '14',
            },
          ]
        : walletTypes
            ?.filter((option) => [9, 10].includes(option.value))
            .map((option) => ({
              label: t(`pages.currentProfiles.actionUser.addWallet.form.walletTypeOptions.${option.value}`),
              value: option.value?.toString(),
            })),
    [defaultValues.walletTypeId, t, walletTypes],
  );

  const bicOptions: TOption[] = useMemo(
    () =>
      userBics.map((iban) => ({
        label: authorizedBic[iban],
        value: authorizedBic[iban],
      })),
    [userBics],
  );

  return {
    walletTypeOptions,
    bicOptions,
    isFormDisabled,
    canAddWallet,
    isBicEditable: bicOptions?.length > 1,
    isWalletTypeEditable: walletTypeOptions?.length > 1,
    watch,
    setValue,
    getValues,
  };
}
